import React, { useState, useMemo, useEffect } from "react";
import { Button, Column, Icon, Row, Text } from "@amzn/meridian";
import { useNavigate } from "react-router-dom";
import QuestionsList from "./questionsList";
import { useAppDispatch, useAppSelector } from "src/store/store";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import checkSmallTokens from "@amzn/meridian-tokens/base/icon/check-small";
import ProgressBreadcrumb from "src/components/UI/ProgressBreadcrumb";
import { COLORS } from "src/constants/colors";
import { isEngineerUser } from "src/constants/constants";
import { createNewInspection, getInspectionById } from "src/store/inspectionSlice";
import { setActiveQuestion, setActiveObjectQuestions, setImageCapturingMode, setStreamingMode, setStreamingDevMode, setStreamingConfig, setScreenDimensions, setCameraOpen, setReadyToSubmitReport, resetInspectionCaptureState, clearBarcodeData } from "src/store/inspectionCaptureSlice";
import { resetInspectionResults, setInspectionResults } from "src/store/inspectionResultsSlice";
import ModeSelectionSheet from "./modeSelectionSheet";
import StreamingConfigDialog from "./StreamingConfigDialog";
import ObjectInstanceModal from "./ObjectInstanceModal";
import { Question } from "src/data/types";
import { groupQuestionsByObjectType, getObjectInstanceDetails } from "src/components/pages/inspection/utils";

/**
 * Helper function to determine if the inspection is for drums based on selected object type
 */
const isDrumBasedInspection = (objectType: string): boolean => {
  if (!objectType) return false;
  
  // Check if the selected object type is "drum" (case-insensitive)
  return objectType.toLowerCase() === "drum";
};

const QuestionsPage = () => {
  const { selectedInspectionTemplate } = useAppSelector((state) => state.inspectionTemplate);
  const { inspectionResults } = useAppSelector((state) => state.inspectionResults);
  const { currentCreatedInspection } = useAppSelector((state) => state.inspection);
  const userLdapGroups = useAppSelector((state) => state.user.ldap);
  const isEngineer = useMemo(() => isEngineerUser(userLdapGroups), [userLdapGroups]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Set dynamic screen dimensions based on actual screen size with smart pixel ratio
  useEffect(() => {
    const updateScreenDimensions = () => {
      const screenW = window.screen.width;
      const screenH = window.screen.height;
      
      // Use device pixel ratio, but force 3x for screens smaller than 1080p
      // This ensures mobile devices and small screens get high-resolution camera
      let pixelRatio = window.devicePixelRatio || 1;
      if (screenW < 1920 || screenH < 1080) {
        pixelRatio = 3;
      }
      
      let cameraWidth = Math.floor(screenW * pixelRatio);
      let cameraHeight = Math.floor(screenH * pixelRatio);
      
      // Swap to landscape orientation for camera (always wider than tall)
      // This prevents zoom issues on portrait devices while maintaining high resolution
      const isPortrait = cameraHeight > cameraWidth;
      if (isPortrait) {
        [cameraWidth, cameraHeight] = [cameraHeight, cameraWidth];
      }
      
      dispatch(setScreenDimensions({
        width: cameraWidth,
        height: cameraHeight,
      }));
    };
    
    // Initial capture
    updateScreenDimensions();
    
    // Update on window resize
    window.addEventListener('resize', updateScreenDimensions);
    
    return () => {
      window.removeEventListener('resize', updateScreenDimensions);
    };
  }, [dispatch]);
  
  // Poll for inspection updates when page is active
  useEffect(() => {
    if (!currentCreatedInspection.inspectionId) return;
    
    // Fetch and update results
    const fetchAndUpdate = async () => {
      const result = await dispatch(getInspectionById(currentCreatedInspection.inspectionId)).unwrap();
      // Update inspectionResults in Redux so questions page reflects latest data
      if (result.results) {
        dispatch(setInspectionResults(result.results));
      }
    };
    
    // Initial fetch on mount
    fetchAndUpdate();
    
    // Poll every 3 seconds for result updates
    const pollInterval = setInterval(() => {
      fetchAndUpdate();
    }, 3000);
    
    return () => clearInterval(pollInterval);
  }, [currentCreatedInspection.inspectionId, dispatch]);
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [showStreamingConfig, setShowStreamingConfig] = useState(false);
  const [showObjectInstanceModal, setShowObjectInstanceModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [selectedObjectType, setSelectedObjectType] = useState<string>("");
  const [selectedObjectQuestions, setSelectedObjectQuestions] = useState<Question[]>([]);
  
  // Calculate progress - count unique questions with results
  const answeredQuestionIds = new Set(inspectionResults.map(r => r.questionId));
  const answeredCount = answeredQuestionIds.size;
  const totalCount = selectedInspectionTemplate?.questions?.length || 0;
  const allAnswered = answeredCount === totalCount && totalCount > 0;

  // Group questions by object type if applicable
  const processedQuestions = useMemo(() => {
    if (!selectedInspectionTemplate?.questions) {
      return { groups: {}, individuals: [] };
    }
    return groupQuestionsByObjectType(selectedInspectionTemplate.questions);
  }, [selectedInspectionTemplate]);

  // Handle question selection - set active and show mode selection
  const handleQuestionSelection = (question: Question) => {
    // Create inspection if not already created
    if (!currentCreatedInspection.inspectionId && selectedInspectionTemplate) {
      // Reset state from previous inspection
      dispatch(resetInspectionResults());
      dispatch(resetInspectionCaptureState());
      
      dispatch(
        createNewInspection({
          templateId: selectedInspectionTemplate.templateId,
          templateName: selectedInspectionTemplate.templateName,
          siteCode: selectedInspectionTemplate.siteCode || "ABE2",
        })
      );
    }
    
    setSelectedQuestion(question);
    dispatch(setActiveQuestion(question));
    dispatch(setActiveObjectQuestions([]));
    setShowModeSelection(true);
  };

  const handleModeSelection = (mode: "standard" | "stream" | "stream-dev") => {
    const isDrum = isDrumBasedInspection(selectedObjectType);

    if (mode === "standard") {
      dispatch(setImageCapturingMode(true));
      dispatch(setStreamingMode(false));
      dispatch(setCameraOpen(true));
      setShowModeSelection(false);
      
      // For drum inspections, navigate to barcode scanner first
      if (isDrum) {
        // Clear any previous barcode data
        dispatch(clearBarcodeData());
        navigate(`/templates/${selectedInspectionTemplate?.templateId}/barcode-scan`);
      } else {
        // For non-drum inspections, go directly to inspection page
        navigate(`/templates/${selectedInspectionTemplate?.templateId}/inspection`);
      }
    } else if (mode === "stream") {
      dispatch(setStreamingMode(true));
      dispatch(setStreamingDevMode(false));
      dispatch(setImageCapturingMode(false));
      dispatch(setCameraOpen(true));
      setShowModeSelection(false);
      
      // For drum inspections, navigate to barcode scanner first
      if (isDrum) {
        // Clear any previous barcode data
        dispatch(clearBarcodeData());
        navigate(`/templates/${selectedInspectionTemplate?.templateId}/barcode-scan`);
      } else {
        navigate(`/templates/${selectedInspectionTemplate?.templateId}/inspection`);
      }
    } else if (mode === "stream-dev") {
      setShowModeSelection(false);
      setShowStreamingConfig(true);
    }
  };

  const handleStreamingConfigConfirm = (config: { resolutionScale: number; quality: number }) => {
    if (selectedInspectionTemplate) {
      // Store config in Redux with resolutionScale
      dispatch(setStreamingConfig({
        quality: config.quality,
        resolutionScale: config.resolutionScale,
        interval: 800, // Keep default interval
      }));

      // Create inspection and start
      dispatch(
        createNewInspection({
          templateId: selectedInspectionTemplate.templateId,
          templateName: selectedInspectionTemplate.templateName,
          siteCode: selectedInspectionTemplate.siteCode || "ABE2",
        })
      );
      dispatch(
        setActiveQuestion(selectedInspectionTemplate?.questions[0])
      );
      dispatch(setStreamingMode(true));
      dispatch(setStreamingDevMode(true)); // Dev mode - enable dev controls
      dispatch(setImageCapturingMode(false));
      
      navigate(
        `/templates/${selectedInspectionTemplate?.templateId}/inspection`
      );
    }
  };

  // Handle object group selection - opens modal showing instances
  const handleObjectGroupSelection = (objectType: string, questions: Question[]) => {
    // Create inspection if not already created
    if (!currentCreatedInspection.inspectionId && selectedInspectionTemplate) {
      // Reset state from previous inspection
      dispatch(resetInspectionResults());
      dispatch(resetInspectionCaptureState());
      
      dispatch(
        createNewInspection({
          templateId: selectedInspectionTemplate.templateId,
          templateName: selectedInspectionTemplate.templateName,
          siteCode: selectedInspectionTemplate.siteCode || "ABE2",
        })
      );
    }

    setSelectedObjectType(objectType);
    setSelectedObjectQuestions(questions);
    setShowObjectInstanceModal(true);
  };

  // Handle inspecting a new instance from the modal
  const handleInspectInstance = (instanceNumber: number) => {
    // Store ALL object questions for multi-question API call
    console.log(selectedObjectQuestions);
    if (selectedObjectQuestions.length > 0) {
      dispatch(setActiveQuestion(selectedObjectQuestions[0]));
      dispatch(setActiveObjectQuestions(selectedObjectQuestions)); // Store all questions
    }
    
    setShowObjectInstanceModal(false);
    setShowModeSelection(true);
  };

  // Get instance details for modal
  const objectInstanceDetails = useMemo(() => {
    if (!selectedObjectType || selectedObjectQuestions.length === 0) {
      return { instanceCount: 0, instances: [] };
    }
    return getObjectInstanceDetails(
      selectedObjectType,
      selectedObjectQuestions as any,
      inspectionResults as any
    );
  }, [selectedObjectType, selectedObjectQuestions, inspectionResults]);

  return (
    <Column
      className="questions-page"
      height="100vh"
      alignmentVertical="justify"
      backgroundColor={COLORS.DARKER_WHITE}
    >
      <Column>
        <Column
          width="100%"
          backgroundColor={COLORS.WHITE}
          spacingInset="600 500 400"
        >
          <Row>
            <div
              style={{ cursor: "pointer", marginLeft: "50px" }}
              onClick={() => navigate("/templates")}
            >
              <Icon tokens={chevronLeftLargeTokens} color="primary" />
            </div>
            <Text type="h200">Inspection</Text>
          </Row>
          <Column>
            <Row>
              {selectedInspectionTemplate?.templateName && (
                <Text type="b500">
                  {selectedInspectionTemplate?.templateName}
                </Text>
              )}
            </Row>
          </Column>
        </Column>
        <Column spacingInset="400 500">
          {/* Progress Breadcrumb */}
          <ProgressBreadcrumb 
            steps={["Answer Questions", "Review Results", "Submit"]}
            currentStep={0}
          />
          
          <Text type="h300">Select a Question to Inspect!#@!#!@</Text>
          <Text color="secondary">
            {answeredCount} of {totalCount} questions answered
          </Text>
        </Column>
        <Column spacingInset="none 500">
          <QuestionsList 
            onQuestionSelect={handleQuestionSelection}
            onObjectGroupSelect={handleObjectGroupSelection}
            processedQuestions={processedQuestions as any}
          />
        </Column>
      </Column>
      <Column spacingInset="500" spacing="300">
        {allAnswered && (
          <>
            {/* Success Message */}
            <div style={{ 
              padding: "16px", 
              backgroundColor: "#F0F9FF", 
              borderRadius: "8px", 
              border: "1px solid #D4E9FF" 
            }}>
              <Column spacing="200">
                <Row spacing="200" alignmentVertical="center">
                  <Icon tokens={checkSmallTokens} color="success" />
                  <Text type="b300" color="success">All questions have been answered!</Text>
                </Row>
                <Text type="b100" color="secondary">
                  Please proceed to review your inspection results before final submission.
                </Text>
              </Column>
            </div>
            
            {/* Continue Button */}
            <Button
              type="primary"
              size="large"
              onClick={() => {
                dispatch(setReadyToSubmitReport(true));
                navigate(`/templates/${selectedInspectionTemplate?.templateId}/inspection`);
              }}
            >
              Continue to Review
            </Button>
            <Row alignmentHorizontal="center">
              <Text type="b100" color="secondary">
                Step 1 of 2
              </Text>
            </Row>
          </>
        )}
      </Column>

      <ModeSelectionSheet
        open={showModeSelection}
        onClose={() => setShowModeSelection(false)}
        onSelectMode={handleModeSelection}
        isEngineer={isEngineer}
      />
      
      <StreamingConfigDialog
        open={showStreamingConfig}
        onClose={() => {
          setShowStreamingConfig(false);
          setShowModeSelection(true); // Go back to mode selection
        }}
        onConfirm={handleStreamingConfigConfirm}
      />

      <ObjectInstanceModal
        open={showObjectInstanceModal}
        objectType={selectedObjectType}
        questions={selectedObjectQuestions}
        completedInstances={objectInstanceDetails.instances}
        onClose={() => setShowObjectInstanceModal(false)}
        onInspectInstance={handleInspectInstance}
      />
    </Column>
  );
};

export default QuestionsPage;
